import React, { useEffect, useState } from "react";
import { authFetch } from "../utils/authFetch";

const API_URL = process.env.REACT_APP_API_URL || "";

const toDateTimeLocal = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const DAYS = [
  { value: "0", label: "Lun" },
  { value: "1", label: "Mar" },
  { value: "2", label: "Mer" },
  { value: "3", label: "Gio" },
  { value: "4", label: "Ven" },
  { value: "5", label: "Sab" },
  { value: "6", label: "Dom" },
];

const WalletCampaignManager = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [name, setName] = useState("");
  const [bonusPercent, setBonusPercent] = useState("");
  const [bonusFixedAmount, setBonusFixedAmount] = useState("");
  const [minRechargeAmount, setMinRechargeAmount] = useState("");
  const [givesPoints, setGivesPoints] = useState(false);
  const [onlyFirstRecharge, setOnlyFirstRecharge] = useState(false);
  const [daysOfWeek, setDaysOfWeek] = useState([]);
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadCampaigns = async () => {
    const res = await authFetch(`${API_URL}/api/wallet/campaigns/`);
    const json = await res.json();
    if (res.ok) setCampaigns(json.campaigns || []);
  };

  useEffect(() => {
    loadCampaigns();
  }, []);

  const resetForm = () => {
    setName("");
    setBonusPercent("");
    setBonusFixedAmount("");
    setMinRechargeAmount("");
    setGivesPoints(false);
    setOnlyFirstRecharge(false);
    setDaysOfWeek([]);
    setStartsAt("");
    setEndsAt("");
    setEditingId(null);
  };

  const toggleDay = (day) => {
    setDaysOfWeek((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const saveCampaign = async () => {
    if (!name) {
      alert("Inserisci il nome della promo.");
      return;
    }

    const percent = Number(bonusPercent || 0);
    const fixed = Number(bonusFixedAmount || 0);

    if (percent <= 0 && fixed <= 0) {
      alert("Inserisci almeno una percentuale o un bonus fisso.");
      return;
    }

    setLoading(true);

    const url = editingId
      ? `${API_URL}/api/wallet/campaigns/${editingId}/`
      : `${API_URL}/api/wallet/campaigns/`;

    const res = await authFetch(url, {
      method: editingId ? "PATCH" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name,
        bonus_percent: bonusPercent || "0",
        bonus_fixed_amount: bonusFixedAmount || "0",
        min_recharge_amount: minRechargeAmount || "0",
        gives_points: givesPoints,
        only_first_recharge: onlyFirstRecharge,
        days_of_week: daysOfWeek,
        starts_at: startsAt || null,
        ends_at: endsAt || null,
        is_active: true,
      }),
    });

    if (res.ok) {
      resetForm();
      loadCampaigns();
    } else {
      const json = await res.json().catch(() => ({}));
      alert(json.detail || "Errore salvataggio promo.");
    }

    setLoading(false);
  };

  const startEdit = (campaign) => {
    setEditingId(campaign.id);
    setName(campaign.name || "");
    setBonusPercent(campaign.bonus_percent || "");
    setBonusFixedAmount(campaign.bonus_fixed_amount || "");
    setMinRechargeAmount(campaign.min_recharge_amount || "");
    setGivesPoints(!!campaign.gives_points);
    setOnlyFirstRecharge(!!campaign.only_first_recharge);
    setDaysOfWeek((campaign.days_of_week || []).map(String));
    setStartsAt(toDateTimeLocal(campaign.starts_at));
    setEndsAt(toDateTimeLocal(campaign.ends_at));
  };

  const toggleCampaign = async (id, active) => {
    await authFetch(`${API_URL}/api/wallet/campaigns/${id}/`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ is_active: active }),
    });
    loadCampaigns();
  };

  const deleteCampaign = async (campaign) => {
    const ok = window.confirm(`Eliminare la promo "${campaign.name}"?`);
    if (!ok) return;

    const res = await authFetch(`${API_URL}/api/wallet/campaigns/${campaign.id}/`, {
      method: "DELETE",
    });

    if (res.ok) {
      if (editingId === campaign.id) resetForm();
      loadCampaigns();
    } else {
      alert("Errore eliminazione promo.");
    }
  };

  const campaignDetails = (c) => {
    const details = [];
    if (Number(c.bonus_percent || 0) > 0) details.push(`+${c.bonus_percent}%`);
    if (Number(c.bonus_fixed_amount || 0) > 0) details.push(`+€${c.bonus_fixed_amount} fisso`);
    if (Number(c.min_recharge_amount || 0) > 0) details.push(`min €${c.min_recharge_amount}`);
    if (c.gives_points) details.push("genera punti");
    else details.push("non genera punti");
    if (c.only_first_recharge) details.push("solo prima ricarica");
    if ((c.days_of_week || []).length) {
      const labels = (c.days_of_week || [])
        .map((d) => DAYS.find((x) => x.value === String(d))?.label)
        .filter(Boolean)
        .join(", ");
      details.push(labels);
    }
    return details.join(" • ");
  };

  return (
    <div className="card shadow-sm border-0 mb-4">
      <div className="card-body">
        <h4>🎯 Promo Wallet</h4>
        <p className="text-muted small mb-3">
          Crea promo percentuali o con bonus fisso. Le percentuali e gli importi sono sempre decisi dal gestore.
        </p>

        <div className="row g-2 mt-2">
          <div className="col-md-4">
            <label className="form-label small">Nome promo</label>
            <input
              className="form-control"
              placeholder="Es. Happy Hour Febbraio"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="col-md-2">
            <label className="form-label small">Bonus %</label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              className="form-control"
              placeholder="Es. 10"
              value={bonusPercent}
              onChange={(e) => setBonusPercent(e.target.value)}
            />
          </div>

          <div className="col-md-2">
            <label className="form-label small">Bonus fisso €</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="form-control"
              placeholder="Es. 5"
              value={bonusFixedAmount}
              onChange={(e) => setBonusFixedAmount(e.target.value)}
            />
          </div>

          <div className="col-md-2">
            <label className="form-label small">Ricarica minima €</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="form-control"
              placeholder="Es. 50"
              value={minRechargeAmount}
              onChange={(e) => setMinRechargeAmount(e.target.value)}
            />
          </div>
        </div>

        <div className="row g-2 mt-3">
          <div className="col-md-4">
            <div className="form-check">
              <input
                id="givesPoints"
                className="form-check-input"
                type="checkbox"
                checked={givesPoints}
                onChange={(e) => setGivesPoints(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="givesPoints">
                Il credito genera punti
              </label>
            </div>
            <div className="small text-muted">
              Utile per bonus fisso / nuovo cliente / happy hour leggero.
            </div>
          </div>

          <div className="col-md-4">
            <div className="form-check">
              <input
                id="onlyFirstRecharge"
                className="form-check-input"
                type="checkbox"
                checked={onlyFirstRecharge}
                onChange={(e) => setOnlyFirstRecharge(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="onlyFirstRecharge">
                Solo prima ricarica cliente
              </label>
            </div>
            <div className="small text-muted">
              Ideale per promo nuovo cliente.
            </div>
          </div>
        </div>

          <div className="row g-2 mt-3">
            <div className="col-md-3">
              <label className="form-label small">Inizio promo</label>
              <input
                type="datetime-local"
                className="form-control"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label small">Fine promo</label>
              <input
                type="datetime-local"
                className="form-control"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
              />
            </div>

            <div className="col-12 small text-muted">
              Se lasci vuote le date, la promo resta valida finché è attiva. Se imposti inizio/fine, partirà e si fermerà automaticamente.
            </div>
          </div>

        <div className="mt-3">
          <div className="form-label small mb-1">Giorni validi</div>
          <div className="d-flex flex-wrap gap-2">
            {DAYS.map((d) => (
              <button
                key={d.value}
                type="button"
                className={`btn btn-sm ${daysOfWeek.includes(d.value) ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => toggleDay(d.value)}
              >
                {d.label}
              </button>
            ))}
          </div>
          <div className="small text-muted mt-1">
            Se non scegli giorni, la promo è valida ogni giorno finché resta attiva.
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 mt-3">
          <button className="btn btn-success" onClick={saveCampaign} disabled={loading}>
            {editingId ? "Aggiorna promo" : "Salva promo"}
          </button>

          {editingId && (
            <button className="btn btn-outline-secondary" onClick={resetForm}>
              Annulla modifica
            </button>
          )}
        </div>

        <hr />

        <h6>Promo esistenti</h6>

        {campaigns.length === 0 && (
          <div className="text-muted small">Nessuna promo creata.</div>
        )}

        {campaigns.map((c) => (
          <div key={c.id} className="d-flex justify-content-between align-items-center border rounded p-2 mb-2">
            <div>
              <strong>{c.name}</strong>
              {c.is_active && <span className="badge bg-success ms-2">ATTIVA</span>}
              <div className="small text-muted">{campaignDetails(c)}</div>
            </div>

            <div className="d-flex gap-2">
              <button
                className={`btn btn-sm ${c.is_active ? "btn-success" : "btn-outline-secondary"}`}
                onClick={() => toggleCampaign(c.id, !c.is_active)}
              >
                {c.is_active ? "Disattiva" : "Attiva"}
              </button>

              <button className="btn btn-sm btn-outline-primary" onClick={() => startEdit(c)}>
                Modifica
              </button>

              <button className="btn btn-sm btn-outline-danger" onClick={() => deleteCampaign(c)}>
                Elimina
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WalletCampaignManager;
